/**
 * Related Products Swiper Handler
 * Manages carousel functionality for dynamically loaded product recommendations
 */
class RelatedProductsSwiper {
  constructor() {
    this.swiperInstance = null;
    this.observer = null;
    this.checkInterval = null;
    this.selectors = {
      container: ".mc-related-products__wrapper",
      productRecommendations: "product-recommendations.mc-related-products",
      prevBtn: ".mc-related-products__prev",
      nextBtn: ".mc-related-products__next",
    };

    this.config = {
      slidesPerView: 1,
      spaceBetween: 20,
      loop: false,
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
      },
      speed: 600,
      navigation: {
        nextEl: this.selectors.nextBtn,
        prevEl: this.selectors.prevBtn,
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      touchRatio: 1,
      touchAngle: 45,
      simulateTouch: true,
      watchSlidesProgress: true,
      breakpoints: {
        567: {
          slidesPerView: 2,
          spaceBetween: 20,
        },
        960: {
          slidesPerView: 3,
          spaceBetween: 30,
        },
        1199: {
          slidesPerView: 4,
          spaceBetween: 30,
        },
      },
      on: {
        init: () => this.updateNavigationState(),
        slideChange: () => this.updateNavigationState(),
        reachBeginning: () => this.updateNavigationState(),
        reachEnd: () => this.updateNavigationState(),
      },
    };

    this.init();
  }

  init() {
    document.addEventListener("DOMContentLoaded", () =>
      this.initializeSwiper()
    );
    this.setupDynamicContentWatcher();
    this.setupClassWatcher();
  }

  initializeSwiper() {
    const container = document.querySelector(this.selectors.container);

    if (!container || container.swiper) {
      return false;
    }

    try {
      this.swiperInstance = new Swiper(this.selectors.container, this.config);
      return true;
    } catch (error) {
      console.error("Failed to initialize Related Products Swiper:", error);
      return false;
    }
  }

  updateNavigationState() {
    if (!this.swiperInstance) return;

    const prevBtn = document.querySelector(this.selectors.prevBtn);
    const nextBtn = document.querySelector(this.selectors.nextBtn);

    if (prevBtn) {
      prevBtn.disabled = this.swiperInstance.isBeginning;
      prevBtn.setAttribute("aria-disabled", this.swiperInstance.isBeginning);
    }

    if (nextBtn) {
      nextBtn.disabled = this.swiperInstance.isEnd;
      nextBtn.setAttribute("aria-disabled", this.swiperInstance.isEnd);
    }
  }

  setupDynamicContentWatcher() {
    this.observer = new MutationObserver((mutations) => {
      const hasNewSwiper = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            node.querySelector &&
            node.querySelector(this.selectors.container)
        )
      );

      if (hasNewSwiper) {
        setTimeout(() => this.initializeSwiper(), 100);
      }
    });

    const productRecommendations = document.querySelector(
      this.selectors.productRecommendations
    );
    if (productRecommendations) {
      this.observer.observe(productRecommendations, {
        childList: true,
        subtree: true,
      });
    }
  }

  setupClassWatcher() {
    this.checkInterval = setInterval(() => {
      const productRecs = document.querySelector(
        this.selectors.productRecommendations
      );

      if (
        productRecs &&
        productRecs.classList.contains("product-recommendations--loaded")
      ) {
        this.initializeSwiper();
        this.cleanup();
      }
    }, 500);

    // Auto cleanup after 10 seconds
    setTimeout(() => this.cleanup(), 10000);
  }

  cleanup() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  destroy() {
    this.cleanup();

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }
}

// Initialize the Related Products Swiper
new RelatedProductsSwiper();
